import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Calendar, Clock, Star, User, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

export default function EmploymentHistory() {
  const navigate = useNavigate();
  const [employments, setEmployments] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchEmployments();
    fetchPendingReviews();
  }, []);

  const fetchEmployments = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/employments`, {
        withCredentials: true
      });
      setEmployments(response.data);
    } catch (error) {
      console.error('Failed to fetch employments');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingReviews = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/employments/pending-reviews`, {
        withCredentials: true
      });
      setPendingReviews(response.data);
    } catch (error) {
      console.error('Failed to fetch pending reviews');
    }
  };

  const getStatusBadge = (status, endDate) => {
    const isOverdue = new Date(endDate) < new Date();
    
    if (status === 'completed') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <CheckCircle className="h-3 w-3" />
          Completed
        </span>
      );
    } else if (status === 'cancelled') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
          <XCircle className="h-3 w-3" />
          Cancelled
        </span>
      );
    } else if (isOverdue) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
          <AlertCircle className="h-3 w-3" />
          Ended
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
          <Clock className="h-3 w-3" />
          Active
        </span>
      );
    }
  };

  const filteredEmployments = employments.filter(emp => {
    if (filter === 'all') return true;
    if (filter === 'active') return emp.status === 'active' && new Date(emp.end_date) >= new Date();
    if (filter === 'completed') return emp.status === 'completed' || new Date(emp.end_date) < new Date();
    return true;
  });

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Pending Reviews Alert */}
      {pendingReviews.length > 0 && (
        <div className="card bg-yellow-50 border-yellow-200" data-testid="pending-reviews-alert">
          <div className="flex items-start gap-3">
            <Star className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-yellow-800">Reviews Pending</h4>
              <p className="text-sm text-yellow-700 mt-1">
                You have {pendingReviews.length} completed job{pendingReviews.length > 1 ? 's' : ''} waiting for a review.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {pendingReviews.slice(0, 3).map((emp) => (
                  <button
                    key={emp.employment_id}
                    onClick={() => navigate(`/profile/${emp.tradesperson_id}`)}
                    className="text-sm bg-yellow-100 hover:bg-yellow-200 text-yellow-800 px-3 py-1.5 rounded-lg transition-colors"
                    data-testid={`review-btn-${emp.employment_id}`}
                  >
                    Review {emp.tradesperson_name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Employment History Card */}
      <div className="card" data-testid="employment-history">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-orange-500" />
            Employment History
          </h3>
          <div className="flex gap-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="text-sm"
              data-testid="employment-filter"
            >
              <option value="all">All ({employments.length})</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        {filteredEmployments.length === 0 ? (
          <div className="text-center py-8">
            <Briefcase className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No employment records yet</p>
            <p className="text-sm text-slate-400 mt-1">
              Use the "Mark as Hired" button on a tradesperson to track your hires
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredEmployments.map((emp) => (
              <div
                key={emp.employment_id}
                className="p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
                data-testid={`employment-${emp.employment_id}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex gap-3">
                    <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden">
                      {emp.profile_image ? (
                        <img
                          src={`${BACKEND_URL}${emp.profile_image}`}
                          alt={emp.tradesperson_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="h-6 w-6 text-slate-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{emp.tradesperson_name}</p>
                      <p className="text-sm text-orange-600">{emp.trade}</p>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(emp.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - {new Date(emp.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {emp.duration_weeks < 1 
                            ? `${Math.round(emp.duration_weeks * 7)} day${Math.round(emp.duration_weeks * 7) > 1 ? 's' : ''}`
                            : `${emp.duration_weeks} week${emp.duration_weeks > 1 ? 's' : ''}`
                          }
                        </span>
                      </div>
                      {emp.notes && (
                        <p className="text-sm text-slate-500 mt-1 italic">"{emp.notes}"</p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {getStatusBadge(emp.status, emp.end_date)}
                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate(`/profile/${emp.tradesperson_id}`)}
                        className="text-xs text-slate-600 hover:text-slate-800"
                      >
                        View Profile
                      </button>
                      {!emp.has_review && new Date(emp.end_date) < new Date() && (
                        <button
                          onClick={() => navigate(`/profile/${emp.tradesperson_id}`)}
                          className="text-xs text-orange-600 hover:text-orange-700 font-medium flex items-center gap-1"
                          data-testid={`leave-review-${emp.employment_id}`}
                        >
                          <Star className="h-3 w-3" />
                          Leave Review
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
